import { useId, useMemo } from "react";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Button from "../Buttons/Button";
import ContentRecommendationItem from "./ContentRecommendationItem";
import {
  ANALYZING_CONTENT,
  CONTENT_RECOMMENDATIONS_ALL_COMPLETED,
  CONTENT_RECOMMENDATIONS_BANNER_DESCRIPTION,
  CONTENT_RECOMMENDATIONS_BANNER_TITLE,
  CONTENT_RECOMMENDATIONS_COMPLETED_PROGRESS,
  CONTENT_RECOMMENDATIONS_VIEW_ALL,
  NO_CONTENT_RECOMMENDATIONS,
  SUGGESTED_CREATIVES_PROGRESS,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import {
  GRAY_200,
  GRAY_50,
  GRADIENT_PURPLE_FUCHSIA,
  GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT,
  GREEN_COLOR,
  WHITE_COLOR,
} from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";
import { getContentRecommendationKey } from "../../utils/mediaUrlsAnalysis";

const PREVIEW_RECOMMENDATION_LIMIT = 2;

const PROGRESS_BAR_SX = {
  height: 4,
  borderRadius: 999,
  bgcolor: GRAY_200,
  width: "100%",
  minWidth: 0,
  flex: { sm: 1 },
  "& .MuiLinearProgress-bar": {
    borderRadius: 999,
  },
};

// White fill on the padding box, brand gradient only on the border.
// 2px keeps the stroke readable without dominating the card.
const GRADIENT_CARD_SX = {
  width: "100%",
  boxSizing: "border-box",
  border: "3px solid transparent",
  background: `linear-gradient(${WHITE_COLOR}, ${WHITE_COLOR}) padding-box, ${GRADIENT_PURPLE_FUCHSIA} border-box`,
  borderRadius: BORDER_RADIUS,
  p: 2.5,
};

const PULSE_SX = {
  "@keyframes contentRecommendationsIconPulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.45 },
  },
  animation: "contentRecommendationsIconPulse 1.6s ease-in-out infinite",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

const GradientIconBadge = ({ pulsing = false, children }) => (
  <Box
    aria-hidden
    sx={{
      width: 36,
      height: 36,
      borderRadius: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: GRADIENT_PURPLE_FUCHSIA,
      ...((pulsing && PULSE_SX) || {}),
    }}
  >
    {children}
  </Box>
);

const SuggestedCreativesChip = ({
  validMediaUrlsCount,
  suggestedCreativesCount,
}) => {
  const { t } = useTranslationApp();

  if (
    suggestedCreativesCount === null ||
    suggestedCreativesCount === undefined
  ) {
    return null;
  }

  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.5,
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: 1.4,
        color: "text.primary",
        border: `1px solid ${GRAY_200}`,
        borderRadius: 999,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {t(SUGGESTED_CREATIVES_PROGRESS, {
        current: validMediaUrlsCount,
        total: suggestedCreativesCount,
      })}
    </Box>
  );
};

const ContentRecommendationsBanner = ({
  status,
  contentRecommendations = [],
  completedContentRecommendationKeys = [],
  validMediaUrlsCount = 0,
  suggestedCreativesCount = null,
  onSeeMore = () => {},
  onSelectRecommendation = () => {},
  children,
}) => {
  const { t } = useTranslationApp();
  const progressLabelId = useId();

  const completedSet = useMemo(
    () => new Set(completedContentRecommendationKeys),
    [completedContentRecommendationKeys]
  );

  const previewRecommendations = useMemo(() => {
    const pending = contentRecommendations.filter(
      (recommendation) =>
        !completedSet.has(getContentRecommendationKey(recommendation))
    );
    const completed = contentRecommendations.filter((recommendation) =>
      completedSet.has(getContentRecommendationKey(recommendation))
    );
    return [...pending, ...completed].slice(0, PREVIEW_RECOMMENDATION_LIMIT);
  }, [completedSet, contentRecommendations]);

  const totalCount = contentRecommendations.length;
  const completedCount = contentRecommendations.filter((recommendation) =>
    completedSet.has(getContentRecommendationKey(recommendation))
  ).length;
  const allCompleted = totalCount > 0 && completedCount === totalCount;
  const progressValue =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  switch (status) {
    case "loading":
      return (
        <Box sx={GRADIENT_CARD_SX}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <GradientIconBadge pulsing>
              <AutoAwesomeIcon
                sx={{
                  fontSize: 20,
                  color: `${GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT} !important`,
                }}
              />
            </GradientIconBadge>
            <Typography
              component="p"
              sx={{
                m: 0,
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {t(ANALYZING_CONTENT)}
            </Typography>
          </Box>
          {children ? <Box sx={{ mt: 1.5 }}>{children}</Box> : null}
        </Box>
      );
    case "empty":
      return (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 1.5,
            border: `1px solid ${GRAY_200}`,
            borderRadius: BORDER_RADIUS,
            bgcolor: "background.paper",
          }}
        >
          <CheckCircleOutlineIcon
            aria-hidden
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: "14px",
              lineHeight: 1.5,
              color: "text.secondary",
            }}
          >
            {t(NO_CONTENT_RECOMMENDATIONS)}
          </Typography>
        </Box>
      );
    case "suggestions":
      return (
        <Fade in timeout={300}>
          <Box sx={GRADIENT_CARD_SX}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              {allCompleted ? (
                <Box
                  aria-hidden
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    bgcolor: GRAY_50,
                    border: `1px solid ${GRAY_200}`,
                  }}
                >
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 22,
                      color: `${GREEN_COLOR} !important`,
                    }}
                  />
                </Box>
              ) : (
                <GradientIconBadge>
                  <AutoAwesomeIcon
                    sx={{
                      fontSize: 20,
                      color: `${GRADIENT_PURPLE_FUCHSIA_COLOR_TEXT} !important`,
                    }}
                  />
                </GradientIconBadge>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      m: 0,
                      flex: "1 1 12rem",
                      minWidth: "min(100%, 12rem)",
                      fontSize: "16px",
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {t(
                      allCompleted
                        ? CONTENT_RECOMMENDATIONS_ALL_COMPLETED
                        : CONTENT_RECOMMENDATIONS_BANNER_TITLE
                    )}
                  </Typography>
                  <SuggestedCreativesChip
                    validMediaUrlsCount={validMediaUrlsCount}
                    suggestedCreativesCount={suggestedCreativesCount}
                  />
                </Box>
                {allCompleted ? null : (
                  <Typography
                    component="p"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      fontSize: "13px",
                      lineHeight: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    {t(CONTENT_RECOMMENDATIONS_BANNER_DESCRIPTION)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1.5,
                borderTop: `1px solid ${GRAY_200}`,
                "& > :last-child": { borderBottom: "none" },
              }}
            >
              {previewRecommendations.map((contentRecommendation, index) => {
                const recommendationKey = getContentRecommendationKey(
                  contentRecommendation
                );

                return (
                  <ContentRecommendationItem
                    key={recommendationKey || `content-recommendation-${index}`}
                    contentRecommendation={contentRecommendation}
                    isCompleted={completedSet.has(recommendationKey)}
                    variant="compact"
                    onSelect={onSelectRecommendation}
                  />
                );
              })}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${GRAY_200}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  id={progressLabelId}
                  component="p"
                  sx={{
                    m: 0,
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    whiteSpace: { sm: "nowrap" },
                  }}
                >
                  {t(CONTENT_RECOMMENDATIONS_COMPLETED_PROGRESS, {
                    completed: completedCount,
                    total: totalCount,
                  })}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={allCompleted ? 100 : progressValue}
                  aria-labelledby={progressLabelId}
                  sx={PROGRESS_BAR_SX}
                />
              </Box>
              <Button
                color="secondary"
                size="small"
                type="button"
                endIcon={
                  <ArrowForwardIcon sx={{ fontSize: "18px !important" }} />
                }
                onClick={onSeeMore}
                sx={{
                  height: 36,
                  minWidth: 0,
                  py: "6px",
                  px: 1.5,
                  flexShrink: 0,
                  width: { xs: "100%", sm: "auto" },
                  textTransform: "none",
                }}
              >
                {t(CONTENT_RECOMMENDATIONS_VIEW_ALL, { count: totalCount })}
              </Button>
            </Box>
          </Box>
        </Fade>
      );
    default:
      return null;
  }
};

export default ContentRecommendationsBanner;
